import React, { useState, createContext, useContext, useEffect } from "react";
import { useRouter } from "next/router";
import { useAxios, handleError } from "../utils/helpers";
import { Loader } from "../design";
import { useLoopAuth } from "./auth-provider";
import { APIFunction } from "../utils/apiFunction";
import { CustomerPortalContext } from "../backend/serverProps";

const LoopContext = createContext(null);

export function LoopProvider(props: { children: React.ReactNode; customerPortalContext: CustomerPortalContext, themeNanoId: string }) {

    const router = useRouter();
    const [axios] = useAxios();
    const { user, myshopifyDomain } = useLoopAuth();

    const [isLoading, setisLoading] = useState(true);
    const [token, settoken] = useState("testToken");
    const [_dataLoaded, setdataLoaded] = useState(false);
    const [integrations, setintegrations]: Array<any> = useState({});
    const [contextValue, setcontextValue] = useState({});
    const [mounted, setMounted] = useState(false);

    const generateMultipassLink = async (link: string) => {
        try {
            let _body = {
                redirectTo: `/${link}`,
            };
            const { data }: any = await axios.post(
                `${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/generate_multipass_token`,
                _body,
            );
            return data;
        } catch (error) {
            return { success: false, messageKey: "error_message" };
        }
    };

    const loadData = async (myshopify_domain: string, Shopify: any) => {
        setisLoading(true);
        const _locale = Shopify?.locale || router?.query?.locale || "en";
        const _country = Shopify?.country || router?.query?.country || "US";

        // Use server-side data from customerPortalContext if available
        if (props?.customerPortalContext?.preferences) {
            setintegrations(props.customerPortalContext.integrations || {});
            setcontextValue({
                ...contextValue,
                myshopify_domain: myshopify_domain,
                preferences: props.customerPortalContext.preferences,
                store: props.customerPortalContext.store,
                locale: _locale,
                country: _country,
                localeRoute: Shopify?.routes?.root || "/",
                deliveryPreferences: props.customerPortalContext.deliveryPreferences,
                featureSwitches: props.customerPortalContext.featureSwitches,
                lineCardVersion: props.customerPortalContext.themeMetadata?.lineCardDisplay || "v1",
                showNavigationButtonsUpsell: parseInt(props.customerPortalContext.themeMetadata?.showNavigationButtons ?? 0),
            });
            setdataLoaded(true);
            setisLoading(false);
            return;
        }

        // Fallback to client-side API calls if server-side data not available
        try {
            let _params = { myshopify_domain: myshopify_domain };
            const [{ data: featureSwitches }, { data: prefData }, themePrefs]: Array<any> = await Promise.all([
                axios.get(`${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/featureGate/featureSwitches?shopifyDomain=${myshopify_domain}`),
                axios.get(`${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/read_cp_preferences_v2?` + new URLSearchParams(_params)),
                APIFunction.getThemeMetadata(props?.customerPortalContext?.themeNanoId ?? router?.query?.previewThemeId as string ?? props.themeNanoId),
            ]);
            setintegrations(prefData.data.integrations);
            setcontextValue({
                ...contextValue,
                myshopify_domain: myshopify_domain,
                preferences: prefData.data.preferences,
                store: prefData.data.store,
                locale: _locale,
                country: _country,
                localeRoute: Shopify?.routes?.root || "/",
                deliveryPreferences: prefData.data.deliveryPreferences,
                featureSwitches: featureSwitches.data.featureSwitches,
                lineCardVersion: themePrefs.data.lineCardDisplay || "v1",
                showNavigationButtonsUpsell: parseInt(themePrefs.data.showNavigationButtons ?? 0),
            });
            setdataLoaded(true);
        } catch (error) {
            handleError(error, "components/loop-provider", "loadData", {
                myshopify_domain,
            });
        }
        setisLoading(false);
    };

    useEffect(() => {
        setMounted(true);
        return () => setMounted(false);
    }, []);

    useEffect(() => {
        if (router.isReady && !_dataLoaded && typeof window !== "undefined") {
            const Shopify = (window as any).Shopify;
            const shopifyDomain = props?.customerPortalContext?.shopifyDomain ?? Shopify?.shop ?? process.env.NEXT_PUBLIC_DEV_MYSHOPIFY_DOMAIN;
            if (shopifyDomain) {
                loadData(shopifyDomain, Shopify);
            }
        }
    }, [router, _dataLoaded, props.customerPortalContext.themeNanoId]);

    useEffect(() => {
        if (router.isReady && router.query.customer_shopify_id && user && user.userShopifyId) {
            if (router.query.customer_shopify_id != user.userShopifyId) {
                window.location.href = `https://${myshopifyDomain}${process.env.NEXT_PUBLIC_PROXY_PATH}/customer/${user.userShopifyId}`;
            }
        }
    }, [router, myshopifyDomain, user]);

    useEffect(() => {
        (async () => {
            if (router.isReady && router.query.customer_shopify_id) {
                const ShopifyAnalytics = (window as any).ShopifyAnalytics;
                const _shopifyCustomerId = ShopifyAnalytics?.meta?.page?.customerId || null;
                if (_shopifyCustomerId == null) {
                    setisLoading(false);
                    return;
                }
                if (router.query.customer_shopify_id != _shopifyCustomerId && !!integrations?.multipass) {
                    setisLoading(true);
                    let _url = `${window.location.pathname}${window.location.search}`;
                    const data: any = await generateMultipassLink(_url);
                    if (data?.success) {
                        window.location.href = `/account/login/multipass/${data.data.token}`;
                    }
                    setisLoading(false);
                }
            }
        })();
    }, [router, integrations]);

    return (
        <LoopContext.Provider
            value={{
                ...contextValue,
                customer_shopify_id: user?.userShopifyId,
                customerShopifyId: user?.userShopifyId,
                token,
                integrations,
                generateMultipassLink,
                dataLoaded: _dataLoaded,
                themeNanoId: props.customerPortalContext.themeNanoId,
            }}
        >
            <div className="shopify-section">
                {mounted && !isLoading ? (
                    props.children
                ) : (
                    <div className="loop-background" id="loop-background">
                        {" "}
                        <Loader />
                    </div>
                )}
            </div>
        </LoopContext.Provider>
    );
}

export const useLoop = () => useContext(LoopContext);
